/**
 * EAA-1820: shape for one row of `GET /api/v1/accounts` (EAA-1829,
 * `KeycloakUserSerializer` in portal-api's `directory/org_serializers.py`).
 *
 * Mirrors the real serializer exactly. It used to say "no `name`,
 * `organization` or `level` field exists on this shape because portal-api does
 * not return one" — CORRECTED 2026-08-21 (EAA-1839): the response DOES carry
 * `first_name` and `last_name` today, verified against the live endpoint
 * (`GET /api/v1/accounts?page_size=2` returns e.g.
 * `"first_name": "Thinh", "last_name": "Nguyen"` on every row). They were simply
 * never declared here, which is why this board showed usernames while App Detail
 * -> "Tài khoản" showed real names off the same join. `organization`/`level`
 * remain absent as described — `placement` is the shape that carries that.
 * See AccountsPage.tsx's file-level docstring for the design-frame fields this
 * screen genuinely cannot source.
 */
export interface KeycloakAccountRow {
    sub: string;
    username: string;
    email: string;
    /**
     * EAA-1839: OPTIONAL, and that is not hedging — Keycloak does not require
     * either field, so a real account can carry one, both or neither, and this
     * realm has rows where they are empty and rows where the two are identical
     * ("phuvanschool phuvanschool"). Never render them directly; go through
     * `Profile/accountDisplayName.ts`, which is the one place the
     * name -> username -> email fallback order is decided.
     */
    first_name?: string;
    last_name?: string;
    enabled: boolean;
    /** Keycloak "required actions" — e.g. `UPDATE_PASSWORD` when the temp
     *  password has not been changed yet. Absent, never null, when Keycloak
     *  sends none. */
    required_actions?: string[];
    /** Unix ms epoch account-creation timestamp, or null if Keycloak omitted it. */
    created?: number | null;
    /**
     * EAA-1820: epoch ms of the most recent login, from Keycloak's EVENT store
     * (the user representation has no such field).
     *
     * NULL IS AMBIGUOUS AND THIS ROW CANNOT RESOLVE IT — it means "no login
     * recorded", which covers never-signed-in, signed in longer ago than the
     * server's lookback window, and the realm not recording login events at
     * all. Read it together with `AccountsScanResult.lastLoginAvailable`:
     * false there means the whole column is unknown, not that these people
     * never signed in. Rendering "Chưa bao giờ" off this field alone would be
     * a confident lie on a security screen.
     */
    last_login?: number | null;
    /**
     * EAA-1820: the account's current ACTIVE placement in the directory, or null
     * when it holds none. Null is a REAL state here, not missing data — an
     * account with no assignment signs in and sees nothing, which is exactly
     * what this column exists to surface.
     *
     * `count` is the total number of active placements the person holds, so a
     * cell showing one does not imply it is the only one.
     */
    placement?: {
        organization_id: string;
        organization_name: string;
        level_name: string;
        count: number;
    } | null;
    /**
     * EAA-1820: app-role keys RESOLVED server-side through
     * `Level.role_key -> RoleMap -> AppRole` — the same path provisioning walks,
     * so this is what the person will actually be granted, not a second copy
     * that can drift. Empty for no placement, and also when every link is
     * paused (a paused link grants nothing).
     */
    app_roles?: string[];
    /**
     * EAA-1820: `console` when an AccountAudit create row proves this console
     * minted it; `unknown` otherwise. There is no "file import" value — nothing
     * records that as an account origin, so it is not offered.
     */
    source?: 'console' | 'unknown';
}

export type AccountStatusValue = 'active' | 'pending' | 'locked';
export type AccountPermissionValue = 'granted' | 'none';
export type AccountSourceValue = 'console' | 'unknown';

/**
 * EAA-1820: the realm rail's filters, rewritten for `GET /api/v1/accounts`'
 * REAL params (`status`/`permission`/`organization_id`/`source` — verified
 * against `AccountsView.get`; each takes exactly ONE value, never a list).
 *
 * This REPLACES the previous `AccountFacetFilters` (array-valued, checkbox
 * multi-select within each group), which was built for a client-side walk that
 * could OR several values together in memory. The new endpoint cannot do that
 * — asking for "active OR pending" would take two requests and a client-side
 * merge, which is exactly the "rely on client filtering" pattern this ticket's
 * governing rule forbids. So every group here is single-select, `null` meaning
 * "no constraint" — same shape and same reason as the org screen's
 * `ScopedAccountsFilters`, which is what makes ONE generic `FacetGroup` rail
 * finally correct for both contexts (see `shared/facetTypes.ts`).
 *
 * There is no "unassigned organization" sentinel: the backend has none either
 * (confirmed against `AccountsView` — `organization_id` matches only real,
 * active-Assignment org ids). An account with no placement is reachable through
 * `permission: 'none'` instead, which is what the design's "Chưa gán đơn vị"
 * bucket collapses onto server-side (no active placement ⇒ no resolved role).
 */
export interface RealmAccountsFilters {
    status: AccountStatusValue | null;
    permission: AccountPermissionValue | null;
    source: AccountSourceValue | null;
    organizationId: string | null;
    /** EAA-2068: application code (`?app=`). Single-select like the other rail
     *  groups; a locked app scope (app-detail screen) is NOT stored here — it is
     *  the adapter's `lockedApp` option and cannot be cleared. */
    app: string | null;
}

export const EMPTY_REALM_FILTERS: RealmAccountsFilters = {
    status: null,
    permission: null,
    source: null,
    organizationId: null,
    app: null,
};

/** `username` | `email` | `created` | `status` — exactly `AccountsView`'s
 *  `ordering` values (verified against `account_views.py`). */
export type AccountSortField = 'username' | 'email' | 'created' | 'status';
export type AccountSortDirection = 'asc' | 'desc';

/** `required_actions` containing this means the temp/invite password was never
 *  changed — the closest real proxy this API offers for "never logged in"
 *  (Keycloak's brief user representation carries no last-login field at all).
 *  Moved here (was `useAccountsView.ts`, deleted with the client-side scan) —
 *  still needed by `components/AccountsListTable.tsx` and
 *  `dialogs/AccountDetailModal.tsx` (renamed from `AccountDetailDrawer` when it
 *  became a modal — EAA-1839). */
const PENDING_FIRST_LOGIN_ACTION = 'UPDATE_PASSWORD';

export function isPendingFirstLogin(row: KeycloakAccountRow): boolean {
    return Boolean(row.required_actions?.includes(PENDING_FIRST_LOGIN_ACTION));
}

/** The row's own status, derived — `enabled` + `required_actions`, never
 *  stored on the row itself. */
export function statusOf(row: KeycloakAccountRow): AccountStatusValue {
    if (!row.enabled) return 'locked';
    return isPendingFirstLogin(row) ? 'pending' : 'active';
}
